import { FieldValue } from "firebase/firestore";
import { Klydo } from "./Types";
export type { Klydo } from "./api/klydo";
type BaseType = {
  [key: string]: any;
};

export type ReviewFormStatus = {
  state: string;
  message: string;
};

export type timeTypes = "daily" | "weekly" | "monthly" | "yearly";
export type KlydoTimes = {
  type: timeTypes;
  start: string;
  end: string;
  negative: boolean;
};
export type Stats = BaseType & {
  id: string;
  time?: number;
  favs?: number;
};
export type Review = BaseType & {
  started?: Date;
  ended?: Date;
  type: "delete" | "edit" | "publish";
  date?: Date;
  msg?: string;
};
export type ReviewResponse = BaseType & {
  result: Review;
  review: FieldValue;
  edited?: boolean;
};
export type Version = BaseType & {
  id: string;
  version: number;
  description: string;
  date: Date;
  devices: number;
  deprecated?: boolean;
  isFeature?: boolean;
  // Branch the CI build ran on; missing on versions uploaded before it was sent.
  branch?: string | null;
  // Built from a branch other than main. Set on upload, editable here.
  branchBuild?: boolean;
};
export type Tag = BaseType & {
  id: string;
  name: string;
  total: number;
};
export type General = BaseType & {
  last: number;
  public: number;
  beta: number;
  // Versions at or below this are force-updated by the obsolete-devices cron.
  obsolete?: number;
  autoUser: boolean;
  autoUserCount: number;
  maxMultipleCommand: number;
};
export type Kpi = {
  activeDevices?: number;
  liveDevices?: number;
  connectedDevices?: number;
};
export type TableProps = {
  cols: colType[];
  rowBuild: any;
  onScrollDown?: () => void;
  title: string;
  data: Array<any>;
  cancelSort?: boolean;
  buttons?: any;
  actions?: () => JSX.Element;
  onAction?: (selected: Array<any>) => void;
  // When provided, checking the header "All" checkbox selects every row
  // matching the current filters (fetched without pagination) instead of only
  // the rows currently loaded. Returns the full list to select.
  onCheckAll?: () => Promise<Array<any>>;
  // When true (and onCheckAll is provided), shows a "Select N" control that
  // selects only the first N rows of the onCheckAll result.
  allowPartialSelect?: boolean;
  // When set, the actions button is always visible (disabled with no
  // selection) and shows this label with the selection count.
  actionsLabel?: string;
  actionsIcon?: JSX.Element;
  onSearchChanged?: (currentSearch: string) => void;
  onSelectedChanged?: (
    currSelected: Array<any>,
    currTableData: Array<any>,
  ) => void;
  search?: (row: any, val: string) => boolean;
  initSort?: string;
  modal?: any;
  handleChangedKlydo?: (klydo: Klydo) => void;
  id: string;
  actionDisabled?: (selection: Array<any>) => string | undefined;
  shouldHideSearchBar?: boolean;
  onSortClick?: (field: string) => void;
  param?: string;
  rowHeight?: number;
  // Size the table to the viewport below the app bar, and make wheel-scrolling
  // over it scroll the page first, until the table reaches the top, so content
  // above it (e.g. an open filter panel) can't squeeze the rows.
  fillViewport?: boolean;
};

export type colType = {
  scroll?: boolean;
  size?: number;
  noHeadline?: boolean;
  sort: boolean;
  label: string;
  field: string;
};
export const roles = ["clock", "admin", "editor", "viewer"];

export type User = BaseType & {
  uid: string;
  password?: boolean;
  displayName: string;
  createdAt?: Date;
  used?: Date;
  role: "admin" | "editor" | "clock";
  email: string;
  photoURL?: string;
  socialMedia?: SocialNetwork[];
  basicUserInfo?: { tag: string };
};

// Mirrors the firmware enum (clock-application-v3 NightModeAppearance.kt)
export enum NightModeAppearance {
  RED_THEME = "RED_THEME",
  DIMMED_RED_THEME = "DIMMED_RED_THEME",
  SCREEN_OFF = "SCREEN_OFF",
}

// Mirrors the firmware enum (clock-application-v3 DevMode.kt)
export enum DevMode {
  OFF = "OFF",
  DEV = "DEV",
  ADB_ONLY = "ADB_ONLY",
}

export type Device = BaseType & {
  clientDetails: ClientDetails;
  tags?: string[];
  id: string;
  idf: string;
  serial?: string;
  currentIdf: string;
  heartbeat: string;
  klydo?: { id: string; loopUrl: string };
  clocktime?: {
    local: number;
    server: { time: { value: Date } };
    timeZone?: string;
  };
  timeOffset?: DeviceValue<number>;
  timeFormat?: DeviceValue<string>;
  // ISO timestamp of registerProduct.time, as the API sends it.
  registerProduct?: string;
  version?: number;
  firmware_version?: string;
  location?: {
    latitude: number;
    longitude: number;
    country_code: string;
    city: string;
    region?: string;
    timezone?: string;
  };
  user?: DeviceValue<string>;
  hideDev?: DeviceValue<boolean>;
  devMode?: DeviceValue<DevMode>;
  mode?: DeviceValue<string>;
  sort?: DeviceValue<string>;
  sortDown?: DeviceValue<boolean>;
  show?: DeviceValue<string>;
  feedCycleMode?: DeviceValue<string>;
  collectionCycleMode?: DeviceValue<string>;
  favoritesCycleMode?: DeviceValue<string>;
  showDials?: DeviceValue<boolean>;
  chimes?: DeviceValue<boolean>;
  chimeMode?: DeviceValue<string>;
  chimesVol?: DeviceValue<number>;
  defaultCustomChimeId?: DeviceValue<string>;
  ticks?: DeviceValue<boolean>;
  ticksVol?: DeviceValue<number>;
  quietTimesOn?: DeviceValue<boolean>;
  quietTimes?: {
    source?: string;
    time: Date;
    value: { from: string; until: string };
  };
  auto?: DeviceValue<boolean>;
  autoRestart?: DeviceValue<number>;
  brightness?: DeviceValue<number>;
  meta?: DeviceValue<boolean>;
  nightMode?: { source?: string; time: Date; value: string };
  nightModeAppearance?: DeviceValue<NightModeAppearance>;
  nightModeTimes?: DeviceValue<{ from: string; until: string }>;
  premium?: string[];
  autoCreate: boolean;
  beta?: boolean;
  randomLocation?: { latitude: number; longitude: number };
  orders?: string[];
  hourlyStrikes?: DeviceValue<boolean>;
  freshHold?: DeviceValue<boolean>;
  hardVersion?: { version: number; permanent?: boolean };
  favorites?: DeviceValue<string[]>;
  machineCollections?: DeviceValue<MachineCollection[]>;
  themedCollections?: DeviceValue<MachineCollection[]>;
  curCollection?: DeviceValue<string>;
  curCollections?: DeviceValue<string>;
  curFavs?: DeviceValue<string>;
  curFeed?: DeviceValue<string>;
  isRetail?: DeviceValue<boolean>;
  isKlydosRater?: DeviceValue<boolean>;
  isPreHandledEditionClock?: DeviceValue<boolean>;
  skippedRegistration?: DeviceValue<boolean>;
  showPSF?: DeviceValue<boolean>;
  show13Plus?: DeviceValue<boolean>;
  ecoModeEnabled?: DeviceValue<boolean>;
  /**
   * Config versions the clock has APPLIED, reported with its heartbeat. Plain numbers,
   * not DeviceValue wrappers — they are written straight onto machinesReport.
   */
  configVersion?: number;
  configPersonalVersion?: number;
  playerVolume?: DeviceValue<number>;
  klydosSound?: DeviceValue<boolean>;
  masterSoundMixer?: DeviceValue<{
    ALL?: boolean;
    ANIMATION?: boolean;
    CHIMES_AND_STRIKES?: boolean;
    TICKS?: boolean;
  }>;
  screenState?: DeviceValue<string>;
  feeds?: DeviceValue<string[]>;
  tag?: DeviceValue<string>;
  remoteBattery?: DeviceValue<number>;
  stayInFavorits?: DeviceValue<boolean>;
  hiddenKlydos?: DeviceValue<Array<{ id: string; deletedAt: number }>>;
  pendingKlydos?: DeviceValue<PendingKlydo[]>;
  isArtist?: DeviceValue<boolean>;
  clockReminders?: DeviceValue<ClockReminders>;
  reminderKlydosSettings?: DeviceValue<ReminderKlydosSettings>;
};

export type Reminder = {
  slot: number;
  enabled: boolean;
  time: string;
  repeats: number;
  intervalMinutes: number;
  days: string[];
};

export type ClockReminders = Record<string, Reminder[]>;

export type ReminderKlydoSettings = {
  volume?: number;
  isMuted?: boolean;
  [key: string]: number | boolean | string | undefined;
};

export type ReminderKlydosSettings = Record<string, ReminderKlydoSettings>;

export type ClockReminderSummaryEntry = {
  clockId: string;
  idf?: string;
  version?: number;
  timeOffset?: number;
  location?: {
    country_code?: string;
    city?: string;
    timezone?: string;
  };
  heartbeat?: string;
  updatedAt?: string;
  clockReminders: ClockReminders;
  reminderKlydosSettings: ReminderKlydosSettings;
};

export type MachineCollection = {
  id: string;
  permanent?: boolean;
  themed?: boolean;
};

export type InsertToMode = "FEED" | "FAVORITE" | "COLLECTIONS" | "EXPLORE";

export type InsertTarget = {
  mode: InsertToMode;
  target: string | null;
};

export type PendingKlydo = {
  id: string;
  insertTo: InsertTarget[];
  ttl: number | null;
  insertAt: number;
  playNow: boolean;
  attempts: number;
  forceDownload: boolean;
};

export type ClientDetails = {
  accept: string;
  city: string;
  country: string;
  email: string;
  name: string;
  state: string;
};

export type ClocksListResponse = Pick<
  Device,
  | "id"
  | "idf"
  | "clocktime"
  | "heartbeat"
  | "klydo"
  | "location"
  | "registerProduct"
  | "version"
>;

export type ClockErrorsLog = {
  timestamp: string;
  id: string;
  severity: "ERROR" | "WARNING";
  log: string;
  payload: string;
  version: number;
  exception: string;
  occurrences: number;
};

export type ClockErrorsResponse = {
  data: ClockErrorsLog[];
  total: number;
  pagination: { skip: number; limit: number };
  hasMore: boolean;
};

export type DeviceReport = BaseType &
  Pick<Device, "id" | "heartbeat" | "clocktime" | "randomLocation"> & {
    location: {
      latitude: number;
      longitude: number;
      country_code: string;
      city: string;
      region?: string;
      timezone?: string;
    };
  };
export type ScheduledTask = BaseType & {
  date: Date;
  add: Klydo[];
  remove: Klydo[];
  tag: string;
  commands: Array<Command>;
};

export type Command = BaseType & {
  time: Date;
  local: boolean;
  timeout?: number;
  name: string;
  params?: Array<any>;
  id: string;
  devices?: Array<Device> | string[];
  filter?: Array<string>;
};

export type CommandServer = Command & {
  cmdHistory: Array<CommandHistory>;
};

export type CommandHistory = Command & {
  deviceId: string;
  executeTime: Date;
  receiveTime: Date;
  result: string;
};

export type DeviceValue<T> = {
  time: any;
  value: T;
};

export type ReviewScheme = {
  type: "delete" | "edit" | "publish";
  date: Date;
  msg?: string;
  approved?: boolean;
};

export type SocialNetwork = { network: string; link: string };

export enum SocialNetworkName {
  INSTAGRAM = "Instagram",
  LINKEDIN = "LinkedIn",
  FACEBOOK = "Facebook",
  WEBSITE = "Website",
}
